import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Save, ExternalLink, RotateCcw } from 'lucide-react';
import { Button, Input, CustomSelect } from '../../components/shared';
import { VendorPageHeader } from '../../components/vendor/guidance';
import useOperatingMarkets from '../../hooks/useOperatingMarkets';
import { countryRequiresPostalCode } from '../../utils/countryPostal';
import {
  useUpdateVendorTaxProfile,
  useVendorTaxProfile,
  useVendorStripeConnect,
  vendorStripeConnectApi,
} from '../../services/vendorTaxProfileService';
import { useToastContext } from '../../contexts/ToastContext';
import { useQueryClient } from '@tanstack/react-query';
import StripeConnectStatusBadge from '../../components/shared/StripeConnectStatusBadge';
import { getStripeConnectStatus } from '../../utils/stripeConnectStatus';

const empty = {
  legal_name: '',
  business_name: '',
  business_type: '',
  tax_residence_country: '',
  region_state: '',
  city: '',
  address_line1: '',
  address_line2: '',
  postal_code: '',
  tax_id: '',
  gst_hst_registered: false,
  gst_hst_number: '',
  vat_registered: false,
  vat_number: '',
  us_sales_tax_registered: false,
  w9_status: '',
  payout_country: '',
  payout_currency: 'USD',
};

const BUSINESS_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'sole_prop', label: 'Sole proprietorship' },
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
];

const W9_STATUSES = [
  { value: 'not_collected', label: 'Not collected' },
  { value: 'collected', label: 'Collected' },
  { value: 'not_required', label: 'Not required' },
];

const Hint = ({ children }) => (
  <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{children}</p>
);

const VendorTaxProfile = () => {
  const { success, error: showError } = useToastContext();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading } = useVendorTaxProfile();
  const { data: connectRes, isLoading: connectLoading } = useVendorStripeConnect();
  const connect = connectRes?.data;
  const connectStatus = getStripeConnectStatus(connect);
  const { options: countryOptions, isLoading: marketsLoading, isEmpty } = useOperatingMarkets({
    activeOnly: true,
  });
  const [form, setForm] = useState(empty);
  const [missingFields, setMissingFields] = useState([]);
  const [connectBusy, setConnectBusy] = useState(false);
  const handledConnectFlag = useRef(null);

  const saveMutation = useUpdateVendorTaxProfile({
    onSuccess: (res) => {
      success('Tax profile saved');
      setMissingFields(res?.meta?.missing_required_fields || []);
    },
    onError: (err) => {
      const missing = err?.response?.data?.meta?.missing_required_fields
        || err?.response?.data?.errors?.missing_required_fields
        || [];
      if (missing.length) setMissingFields(missing);
      showError(err?.response?.data?.message || err?.message || 'Could not save tax profile');
    },
  });

  useEffect(() => {
    const row = data?.data;
    if (!row) return;
    setForm({
      legal_name: row.legal_name || '',
      business_name: row.business_name || '',
      business_type: row.business_type || '',
      tax_residence_country: row.tax_residence_country || '',
      region_state: row.region_state || '',
      city: row.city || '',
      address_line1: row.address_line1 || '',
      address_line2: row.address_line2 || '',
      postal_code: row.postal_code || '',
      tax_id: row.tax_id || '',
      gst_hst_registered: Boolean(row.gst_hst_registered),
      gst_hst_number: row.gst_hst_number || '',
      vat_registered: Boolean(row.vat_registered),
      vat_number: row.vat_number || '',
      us_sales_tax_registered: Boolean(row.us_sales_tax_registered),
      w9_status: row.w9_status || '',
      payout_country: row.payout_country || '',
      payout_currency: row.payout_currency || 'USD',
    });
    setMissingFields(data?.meta?.missing_required_fields || []);
  }, [data]);

  useEffect(() => {
    const flag = searchParams.get('connect');
    if (!flag) {
      handledConnectFlag.current = null;
      return;
    }
    if (handledConnectFlag.current === flag) return;
    handledConnectFlag.current = flag;

    const next = new URLSearchParams(searchParams);
    next.delete('connect');
    setSearchParams(next, { replace: true });
    queryClient.invalidateQueries({ queryKey: ['vendor-stripe-connect'] });
    if (flag === 'return') {
      success('Stripe Connect onboarding updated — status refreshed.');
    } else if (flag === 'refresh') {
      showError('Stripe onboarding link expired. Start again below.');
    }
  }, [searchParams, setSearchParams, queryClient, success, showError]);

  const startConnect = async () => {
    setConnectBusy(true);
    try {
      const res = await vendorStripeConnectApi.onboard();
      const url = res?.data?.url;
      if (!url) throw new Error('No onboarding URL returned');
      window.location.href = url;
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || 'Could not start Connect onboarding');
      setConnectBusy(false);
    }
  };

  const resetConnect = async () => {
    if (
      !window.confirm(
        'Remove this Stripe Connect account and start over? In test mode the Express account is deleted from Stripe.'
      )
    ) {
      return;
    }
    setConnectBusy(true);
    try {
      await vendorStripeConnectApi.disconnect({ delete_remote: true });
      await queryClient.invalidateQueries({ queryKey: ['vendor-stripe-connect'] });
      success('Stripe Connect reset. You can start onboarding again.');
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || 'Could not reset Connect');
    } finally {
      setConnectBusy(false);
    }
  };

  const openConnectDashboard = async () => {
    setConnectBusy(true);
    try {
      const res = await vendorStripeConnectApi.dashboardLink();
      const url = res?.data?.url;
      if (!url) throw new Error('No dashboard URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || 'Could not open Stripe dashboard');
    } finally {
      setConnectBusy(false);
    }
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate({ ...form, mark_complete: true });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl">
      <VendorPageHeader
        title="Tax profile"
        subtitle="Who you are for tax, and where you want to be paid. You do not set the tax rate here."
      />

      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">How tax works on a booking</h2>
        <p className="text-sm leading-6 text-gray-700 dark:text-gray-300">
          When a client books you, they pay the marketplace. That payment is split into three parts.
          You only receive the first one.
        </p>
        <ol className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
          <li>
            <span className="font-semibold text-gray-900 dark:text-gray-50">Your service price.</span>{' '}
            This is your earning. It stays held until the event is completed and any wait period on your market has passed.
          </li>
          <li>
            <span className="font-semibold text-gray-900 dark:text-gray-50">Platform fee.</span>{' '}
            This stays with the marketplace. It is not taken out of a tax bill.
          </li>
          <li>
            <span className="font-semibold text-gray-900 dark:text-gray-50">Tax, only if your market collects it.</span>{' '}
            The client pays it at checkout. The marketplace holds it to send to the tax authority. It is not added to your payout, and it is not marketplace profit.
          </li>
        </ol>
        <p className="text-sm leading-6 text-gray-700 dark:text-gray-300">
          The market you work in (Ghana, Canada, the United States, and so on) decides whether tax is charged and which rules apply.
          This page tells us your legal name, where you are based, and whether you are already registered for GST, VAT, or US sales tax.
          A market can mark some of those fields as required before your profile counts as complete.
        </p>
      </section>

      {missingFields.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Still needed for your market: {missingFields.join(', ')}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6 space-y-5"
      >
        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Business</h2>
            <Hint>
              Use the name and address a tax office would recognize. The business name can differ from the legal name when clients know you by a brand.
            </Hint>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Input
                label={missingFields.includes('legal_name') ? 'Legal name *' : 'Legal name'}
                name="legal_name"
                value={form.legal_name}
                onChange={onChange}
                size="sm"
                error={missingFields.includes('legal_name') ? 'Required for your market' : ''}
              />
              <Hint>The name on your ID or tax registration, not a nickname.</Hint>
            </div>
            <Input
              label="Business name"
              name="business_name"
              value={form.business_name}
              onChange={onChange}
              size="sm"
            />
            <CustomSelect
              label="Business type"
              value={form.business_type}
              onChange={(value) => setField('business_type', value)}
              options={BUSINESS_TYPES}
              placeholder="Select…"
              size="sm"
            />
            <div className="space-y-1">
              <CustomSelect
                label={missingFields.includes('tax_residence_country') ? 'Tax residence market *' : 'Tax residence market'}
                value={form.tax_residence_country}
                onChange={(value) => setField('tax_residence_country', value)}
                options={countryOptions}
                placeholder={marketsLoading ? 'Loading markets…' : 'Select market…'}
                size="sm"
                disabled={marketsLoading || isEmpty}
                error={missingFields.includes('tax_residence_country') ? 'Required for your market' : (isEmpty ? 'No markets are set up yet' : '')}
              />
              <Hint>
                The active market where you are based for tax. Only markets an admin has turned on appear here. This choice decides which registration questions matter.
              </Hint>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input name="address_line1" value={form.address_line1} onChange={onChange} placeholder="Address line 1" label="Address line 1" size="sm" />
            <Input name="address_line2" value={form.address_line2} onChange={onChange} placeholder="Address line 2" label="Address line 2" size="sm" />
            <Input name="city" value={form.city} onChange={onChange} placeholder="City" label="City" size="sm" />
            <Input name="region_state" value={form.region_state} onChange={onChange} placeholder="State / province" label="State / province" size="sm" />
            <div className="space-y-1">
              <Input
                name="postal_code"
                value={form.postal_code}
                onChange={onChange}
                placeholder={countryRequiresPostalCode(form.tax_residence_country) ? 'Postal code' : 'Optional'}
                label={countryRequiresPostalCode(form.tax_residence_country) ? 'Postal code' : 'Postal code (optional)'}
                size="sm"
              />
              {!countryRequiresPostalCode(form.tax_residence_country) && (
                <Hint>Many African markets do not use a postal code. You can leave this blank.</Hint>
              )}
            </div>
            <div className="space-y-1">
              <Input name="tax_id" value={form.tax_id} onChange={onChange} placeholder="Tax ID / BN / TIN" label={missingFields.includes('tax_id') ? 'Tax ID / BN / TIN *' : 'Tax ID / BN / TIN'} size="sm" error={missingFields.includes('tax_id') ? 'Required for your market' : ''} />
              <Hint>
                Your government tax number, such as a TIN, BN, or VAT ID. Leave it empty if you do not have one yet, unless your market marks it required.
              </Hint>
            </div>
          </div>
        </section>

        <section className="space-y-3 border-t border-gray-100 dark:border-gray-800 pt-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Registrations</h2>
            <Hint>
              Tick a box only if that tax authority has already registered you. Checking it does not register you, and it does not change the rate your clients are charged.
            </Hint>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="gst_hst_registered" checked={form.gst_hst_registered} onChange={onChange} />
            GST/HST registered (Canada)
          </label>
          <Hint>Canada only. Turn this on when you have a GST or HST number from the Canada Revenue Agency.</Hint>
          {form.gst_hst_registered && (
            <Input name="gst_hst_number" value={form.gst_hst_number} onChange={onChange} placeholder="GST/HST number" label="GST/HST number" size="sm" />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="vat_registered" checked={form.vat_registered} onChange={onChange} />
            VAT registered
          </label>
          <Hint>Use this in markets that charge VAT, such as Ghana, the UK, the EU, Nigeria, or South Africa, and only if you already have a VAT number.</Hint>
          {form.vat_registered && (
            <Input name="vat_number" value={form.vat_number} onChange={onChange} placeholder="VAT number" label="VAT number" size="sm" />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="us_sales_tax_registered"
              checked={form.us_sales_tax_registered}
              onChange={onChange}
            />
            US sales-tax registered
          </label>
          <Hint>United States only. Turn this on if you hold a state sales-tax permit. The marketplace may still collect sales tax from the client.</Hint>
          <div className="space-y-1">
            <CustomSelect
              label="W-9 status (US)"
              value={form.w9_status}
              onChange={(value) => setField('w9_status', value)}
              options={W9_STATUSES}
              placeholder="Not set"
              size="sm"
            />
            <Hint>A W-9 is a US tax form. Choose “Not required” if you do not sell in the United States.</Hint>
          </div>
        </section>

        <section className="space-y-3 border-t border-gray-100 dark:border-gray-800 pt-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Payout</h2>
            <Hint>
              This is where your service earnings should be sent. Tax collected from the client is not included. Most vendor balances are tracked in US dollars even when you operate in another market.
            </Hint>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <CustomSelect
                label="Payout market"
                value={form.payout_country}
                onChange={(value) => setField('payout_country', value)}
                options={countryOptions}
                placeholder={marketsLoading ? 'Loading markets…' : 'Select market…'}
                size="sm"
                disabled={marketsLoading || isEmpty}
              />
              <Hint>Usually the same market as your tax residence. Only active markets are listed.</Hint>
            </div>
            <div className="space-y-1">
              <Input
                label="Payout currency"
                name="payout_currency"
                value={form.payout_currency}
                onChange={onChange}
                maxLength={3}
                size="sm"
              />
              <Hint>Three-letter code, such as USD or GHS. Your earnings ledger is usually kept in USD.</Hint>
            </div>
          </div>
        </section>

        <div className="flex justify-end pt-2">
          <Button type="submit" className="!rounded-xl" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save tax profile
          </Button>
        </div>
      </form>

      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Stripe Connect</h2>
            <p className="text-sm leading-6 text-gray-600 dark:text-gray-400 mt-1">
              This is how released earnings can move to a Stripe account after the event is done. It is not how tax is paid.
              Stripe does not open merchant accounts in most of Africa, including Ghana and Nigeria.
              If you cannot connect, your earnings still build up here. An admin pays you by bank or mobile money and records that payout, without sending you the tax.
            </p>
          </div>
          {!connectLoading ? <StripeConnectStatusBadge connect={connect} /> : null}
        </div>
        {connectLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
        ) : (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Status</dt>
                <dd className="mt-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${connectStatus.className}`}
                  >
                    {connectStatus.label}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Payout ready</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">
                  {connect?.is_payout_ready ? 'Yes' : 'No'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Account</dt>
                <dd className="font-mono text-xs text-gray-700 dark:text-gray-300">
                  {connect?.account_id || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Platform auto-payouts</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">
                  {connect?.platform?.auto_payouts_enabled ? 'Enabled' : 'Off'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Connect country</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">
                  {connect?.resolution?.connect_country || '—'}
                  {connect?.resolution?.source ? (
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">
                      via {connect.resolution.source.replace(/_/g, ' ')}
                      {connect.resolution.connect_currency
                        ? ` · ${connect.resolution.connect_currency}`
                        : ''}
                    </span>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Market</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">
                  {connect?.resolution?.market_country || '—'}
                  {connect?.resolution?.market_currency
                    ? ` · ${connect.resolution.market_currency}`
                    : ''}
                </dd>
              </div>
            </dl>
            {connect?.is_test_mode ? (
              <p className="text-sm text-sky-800 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 rounded-xl px-3 py-2">
                {connect.test_onboarding_hint ||
                  'Test mode is on — use Stripe test identity and bank details in Express onboarding.'}
              </p>
            ) : null}
            {connect?.resolution_error ? (
              <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 rounded-xl px-3 py-2">
                {connect.resolution_error}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                className="!rounded-xl"
                disabled={connectBusy || connect?.can_start_onboarding === false}
                onClick={startConnect}
              >
                {connectBusy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {connect?.account_id ? 'Continue / update onboarding' : 'Start Stripe Connect'}
              </Button>
              {connect?.details_submitted && (
                <Button
                  type="button"
                  variant="secondary"
                  className="!rounded-xl"
                  disabled={connectBusy}
                  onClick={openConnectDashboard}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Stripe Express dashboard
                </Button>
              )}
              {connect?.can_reset ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="!rounded-xl"
                  disabled={connectBusy}
                  onClick={resetConnect}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Remove & start over
                </Button>
              ) : null}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default VendorTaxProfile;
